import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useMeta from "@/hooks/useMeta";
import { Button, Input, Select, Spinner } from "@/components/UI";
import { STATUS_META } from "@/lib/constants";
import TaskCard from "@/components/TaskCard";
import TaskModal from "@/components/TaskModal";
import { Plus, Search } from "lucide-react";

const COLUMNS = ["to_do", "in_progress", "review", "done", "on_hold"];

export default function Kanban() {
  const { user, isAdmin } = useAuth();
  const meta = useMeta();
  const [tasks, setTasks] = useState(null);
  const [filters, setFilters] = useState({ search: "", project_id: "", assignee_id: "" });
  const [modal, setModal] = useState({ open: false, mode: "view", task: null });
  const [dragId, setDragId] = useState(null);

  const load = () => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => toast.error("Gagal memuat tasks"));
  };
  useEffect(load, []);

  const activeSprintId = meta?.sprints?.find((s) => s.status === "active")?.id;

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter((t) => {
      if (!COLUMNS.includes(t.status)) return false;
      const canSee = isAdmin || t.assignee_id === user.id || (["to_do", "backlog"].includes(t.status) && t.assignee_id === null);
      if (!canSee) return false;
      // Board selalu menampilkan sprint aktif (task tanpa sprint tetap tampil)
      if (activeSprintId && t.sprint_id && String(t.sprint_id) !== String(activeSprintId)) return false;
      if (filters.search && !t.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.project_id && String(t.project_id) !== filters.project_id) return false;
      if (filters.assignee_id && String(t.assignee_id) !== filters.assignee_id) return false;
      return true;
    });
  }, [tasks, filters, user, isAdmin, activeSprintId]);

  const grouped = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => (g[c] = []));
    filtered.forEach((t) => {
      if (g[t.status]) g[t.status].push(t);
    });
    // Urutkan tiap kolom berdasarkan deadline terdekat; tanpa deadline paling bawah
    const byDeadline = (a, b) => (a.deadline || "9999-12-31").slice(0, 10).localeCompare((b.deadline || "9999-12-31").slice(0, 10));
    COLUMNS.forEach((c) => g[c].sort(byDeadline));
    return g;
  }, [filtered]);

  const onDrop = async (status) => {
    const task = tasks.find((t) => t.id === dragId);
    setDragId(null);
    if (!task || task.status === status) return;

    const isMine = task.assignee_id === user.id || task.created_by === user.id;
    if (isAdmin && !isMine) {
      toast.error("Kamu hanya bisa memindahkan card milikmu sendiri");
      return;
    }
    if (status === "review" && task.status !== "in_progress" && task.status !== "review") {
      toast.error("Task belum dapat masuk tahap Review. Selesaikan tahap In Progress terlebih dahulu.");
      return;
    }
    if (!isAdmin && status === "done") {
      toast.error("Hanya Admin/PM yang dapat memindahkan ke Done setelah review");
      return;
    }

    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try {
      await api.patch(`/tasks/${task.id}/status`, { status });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal memindahkan");
      load();
    }
  };

  if (!tasks) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="kanban-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Kanban Board</h1>
          <p className="text-sm text-slate-400">Ambil task dari Sprint dan kerjakan hingga Cleared.</p>
        </div>
        <Button onClick={() => setModal({ open: true, mode: "create", task: null })} data-testid="create-task-button">
          <Plus size={16} /> Buat Urgent Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2.5 rounded-md border border-white/10 bg-[#0F172A] p-2.5" data-testid="kanban-filter-toolbar">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input className="pl-9" placeholder="Cari task..." value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="kanban-search" />
        </div>
        <Select className="w-full sm:w-40" value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} data-testid="kanban-filter-project">
          <option value="">Semua Project</option>
          {meta.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}bu
        </Select>
        {isAdmin && (
          <Select className="w-full sm:w-44" value={filters.assignee_id} onChange={(e) => setFilters({ ...filters, assignee_id: e.target.value })} data-testid="kanban-filter-assignee">
            <option value="">Semua PIC</option>
            {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        )}
      </div>

      {/* Board */}
      <div className="flex gap-3 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const meta2 = STATUS_META[col];
          return (
            <div
              key={col}
              data-testid={`kanban-column-${col}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(col)}
              className="flex w-72 shrink-0 flex-col rounded-md border border-white/10 bg-[#0F172A]"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5" style={{ borderTop: `3px solid ${meta2.color}` }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold uppercase tracking-wide text-slate-200">{meta2.label}</span>
                </div>
                <span className="rounded-sm bg-white/5 px-2 py-0.5 font-mono text-xs text-slate-400">{grouped[col].length}</span>
              </div>
              <div className="flex-1 space-y-1 overflow-y-auto p-3" style={{ minHeight: 120, maxHeight: "calc(100vh - 300px)" }}>
                {grouped[col].map((t) => (
                  <div key={t.id} draggable={!isAdmin || t.assignee_id === user.id} onDragStart={() => setDragId(t.id)} onDragEnd={() => setDragId(null)}>
                    <TaskCard task={t} onClick={(task) => setModal({ open: true, mode: "view", task })} />
                  </div>
                ))}
                {grouped[col].length === 0 && (
                  <p className="py-6 text-center text-xs text-slate-600">Kosong</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TaskModal
        open={modal.open}
        mode={modal.mode}
        task={modal.task}
        meta={meta}
        boardMode
        urgentMode
        onClose={() => setModal({ ...modal, open: false })}
        onSaved={load}
      />
    </div>
  );
}
